import { useEffect, useRef } from 'react';
import { AudioDirector } from './AudioDirector.js';
import { HowlerAudioPlayer } from './AudioPlayer.js';
import { SOUND_REGISTRY } from './soundRegistry.js';
import type { LobbySession } from '../session/LobbySession.js';
import type { Preferences } from '../preferences/usePreferences.js';

/**
 * Constructs an AudioDirector for the lifetime of the calling component,
 * subscribes it to the LobbySession snapshot stream, and pushes
 * preference changes into it.
 *
 * Returns nothing — the Director runs as a side effect. Place exactly
 * one call at the top of `RealAppInner`.
 *
 * StrictMode note: this effect runs twice in dev. The cleanup tears the
 * Director down between mounts, so the second mount starts clean — Howl
 * instances are recreated (briefly noisy in dev only).
 */
export function useAudioDirector(
  session: LobbySession,
  preferences: Preferences,
): void {
  const directorRef = useRef<AudioDirector | null>(null);

  useEffect(() => {
    const player = new HowlerAudioPlayer(SOUND_REGISTRY);
    const director = new AudioDirector(player);
    directorRef.current = director;

    // Push initial preferences before subscribing so the first snapshot
    // is processed with the correct volumes.
    director.setVolume('music', preferences.musicVolume);
    director.setVolume('sounds', preferences.soundsVolume);
    director.setReducedMotion(preferences.reducedMotion);

    const unsubscribe = session.subscribe(() => {
      director.onSnapshot(session.getSnapshot());
    });
    // Run once against the current snapshot to hydrate the baseline.
    director.onSnapshot(session.getSnapshot());

    return () => {
      unsubscribe();
      director.dispose();
      directorRef.current = null;
    };
    // `preferences` is intentionally NOT a dep here: the effect below
    // pushes updates into the existing Director instead of rebuilding it
    // on every slider drag. (This repo's eslint config does not include
    // eslint-plugin-react-hooks, so no rule needs suppressing — but if
    // it's added later, this comment is the rationale.)
  }, [session]);

  useEffect(() => {
    directorRef.current?.setVolume('music', preferences.musicVolume);
    directorRef.current?.setVolume('sounds', preferences.soundsVolume);
    directorRef.current?.setReducedMotion(preferences.reducedMotion);
  }, [preferences.musicVolume, preferences.soundsVolume, preferences.reducedMotion]);
}
